import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { useDegradedPolling, useLiveEvent } from '../hooks/useLiveEvents';
import liveOverviewService, { EMPTY_LIVE_OVERVIEW, type LiveOverview } from '../services/liveOverviewService';
import type { CallbackTicket } from '../services/callbackTicketService';
import {
  applyCallbackTicket,
  applyCallLive,
  applyCallStep,
  applyWhatsappMessage,
  applyWhatsappTakeover,
  nextActiveChatExpiry,
  pruneActiveChats,
  sortCallbackTickets,
  sortWaiting,
  type CallLiveEvent,
  type CallStepEvent,
  type OverviewUpdate,
  type WhatsappMessageEvent,
  type WhatsappTakeoverEvent,
} from '@/lib/liveOverviewState';
import { serverNow } from '@/lib/serverClock';
import { withDeadline } from '@/lib/requestDeadline';

/**
 * What is happening right now (waiting customers, running calls, active chats),
 * shared by the bell and the pinned section of Verläufe.
 *
 * The provider loads `GET /api/live/overview` once logged in and keeps it
 * current from the event stream: `whatsapp.takeover`, `whatsapp.message`,
 * `call.live`, `call.step` and `callback_ticket.upsert` are applied locally
 * when the event describes the change completely; otherwise (and on every `ready`, i.e. after a
 * (re)connect) it reloads, throttled. While the stream is degraded it polls.
 * Consumers only use `useLiveOverview()`.
 */
export interface LiveOverviewContextValue {
  overview: LiveOverview;
  /** True until the first overview arrived. */
  loading: boolean;
  /** Refetch now (e.g. after a local takeover/release). */
  refresh: () => void;
  /** A ticket as the server returned it (e.g. after „Erledigt“) — same rules as `callback_ticket.upsert`. */
  applyCallbackTicket: (ticket: CallbackTicket) => void;
}

export const LIVE_OVERVIEW_FALLBACK: LiveOverviewContextValue = {
  overview: EMPTY_LIVE_OVERVIEW,
  loading: false,
  refresh: () => {},
  applyCallbackTicket: () => {},
};

/** At most one overview request per second, however many events ask for one. */
export const OVERVIEW_REFETCH_THROTTLE_MS = 1_000;
/** Slack after an active chat's expiry so the prune never runs a hair too early. */
const EXPIRY_SLACK_MS = 50;

type OverviewUpdater = (overview: LiveOverview) => OverviewUpdate;

function normalize(data: Partial<LiveOverview> | null | undefined): LiveOverview {
  const overview: LiveOverview = {
    waiting: sortWaiting(Array.isArray(data?.waiting) ? data!.waiting : []),
    live_calls: Array.isArray(data?.live_calls) ? data!.live_calls : [],
    active_chats: Array.isArray(data?.active_chats) ? data!.active_chats : [],
    // Older servers do not send it: no tickets.
    callback_tickets: sortCallbackTickets(Array.isArray(data?.callback_tickets) ? data!.callback_tickets : []),
  };
  return pruneActiveChats(overview, serverNow());
}

const LiveOverviewContext = createContext<LiveOverviewContextValue>(LIVE_OVERVIEW_FALLBACK);

export const LiveOverviewProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [overview, setOverview] = useState<LiveOverview>(EMPTY_LIVE_OVERVIEW);
  const [loading, setLoading] = useState<boolean>(isAuthenticated);

  // The ref is the source of truth so an event can decide synchronously
  // whether it needs a refetch; the state only drives rendering.
  const overviewRef = useRef<LiveOverview>(EMPTY_LIVE_OVERVIEW);
  const authedRef = useRef(isAuthenticated);
  /** Bumped on login/logout: a response of the previous session is dropped. */
  const generationRef = useRef(0);
  const inFlightRef = useRef(false);
  const dirtyRef = useRef(false);
  /** Events applied while a request was in flight — re-applied on top of its (older) answer. */
  const pendingRef = useRef<OverviewUpdater[]>([]);
  const lastStartRef = useRef(0);
  const throttleRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const commit = useCallback((next: LiveOverview) => {
    if (next === overviewRef.current) return;
    overviewRef.current = next;
    setOverview(next);
  }, []);

  const scheduleRef = useRef<() => void>(() => {});

  const fetchNow = useCallback(async () => {
    if (!authedRef.current) return;
    if (inFlightRef.current) {
      dirtyRef.current = true;
      return;
    }
    const generation = generationRef.current;
    inFlightRef.current = true;
    dirtyRef.current = false;
    pendingRef.current = [];
    lastStartRef.current = Date.now();
    try {
      const data = await withDeadline(liveOverviewService.getOverview());
      if (generation !== generationRef.current) return;
      const next = pendingRef.current.reduce((acc, apply) => apply(acc).overview, normalize(data));
      commit(next);
    } catch (error) {
      if (generation !== generationRef.current) return;
      // Keep what we have; the next event, `ready` or poll tries again.
      console.warn('Live overview could not be loaded:', error);
    } finally {
      if (generation === generationRef.current) {
        inFlightRef.current = false;
        pendingRef.current = [];
        setLoading(false);
        if (dirtyRef.current) scheduleRef.current();
      }
    }
  }, [commit]);

  const scheduleRefetch = useCallback(() => {
    if (!authedRef.current || throttleRef.current) return;
    const wait = Math.max(0, lastStartRef.current + OVERVIEW_REFETCH_THROTTLE_MS - Date.now());
    throttleRef.current = setTimeout(() => {
      throttleRef.current = null;
      void fetchNow();
    }, wait);
  }, [fetchNow]);
  scheduleRef.current = scheduleRefetch;

  useEffect(() => {
    authedRef.current = isAuthenticated;
    generationRef.current += 1;
    inFlightRef.current = false;
    dirtyRef.current = false;
    pendingRef.current = [];
    lastStartRef.current = 0;
    if (throttleRef.current) clearTimeout(throttleRef.current);
    throttleRef.current = null;
    commit(EMPTY_LIVE_OVERVIEW);
    setLoading(isAuthenticated);
    if (isAuthenticated) void fetchNow();
    return () => {
      if (throttleRef.current) clearTimeout(throttleRef.current);
      throttleRef.current = null;
    };
  }, [isAuthenticated, fetchNow, commit]);

  const applyEvent = useCallback(
    (update: OverviewUpdater) => {
      if (!authedRef.current) return;
      if (inFlightRef.current) pendingRef.current.push(update);
      const result = update(overviewRef.current);
      commit(result.overview);
      if (result.refetch) scheduleRefetch();
    },
    [commit, scheduleRefetch],
  );

  useLiveEvent<WhatsappTakeoverEvent>('whatsapp.takeover', (event) =>
    applyEvent((o) => applyWhatsappTakeover(o, event)),
  );
  useLiveEvent<WhatsappMessageEvent>('whatsapp.message', (event) =>
    applyEvent((o) => applyWhatsappMessage(o, event, serverNow())),
  );
  useLiveEvent<CallLiveEvent>('call.live', (event) => applyEvent((o) => applyCallLive(o, event)));
  useLiveEvent<CallStepEvent>('call.step', (event) => applyEvent((o) => applyCallStep(o, event)));
  useLiveEvent<CallbackTicket>('callback_ticket.upsert', (ticket) =>
    applyEvent((o) => applyCallbackTicket(o, ticket)),
  );
  // (Re)connected: events between our load and the stream may be missing.
  useLiveEvent('ready', () => scheduleRefetch());
  useDegradedPolling(() => void fetchNow());

  // Active chats leave the list 10 minutes after the customer's last message.
  useEffect(() => {
    const wait = nextActiveChatExpiry(overview, serverNow());
    if (wait === null) return undefined;
    const id = setTimeout(() => commit(pruneActiveChats(overviewRef.current, serverNow())), wait + EXPIRY_SLACK_MS);
    return () => clearTimeout(id);
  }, [overview, commit]);

  const refresh = useCallback(() => void fetchNow(), [fetchNow]);
  const applyTicket = useCallback(
    (ticket: CallbackTicket) => applyEvent((o) => applyCallbackTicket(o, ticket)),
    [applyEvent],
  );

  const value = useMemo<LiveOverviewContextValue>(
    () => ({ overview, loading: isAuthenticated && loading, refresh, applyCallbackTicket: applyTicket }),
    [overview, loading, isAuthenticated, refresh, applyTicket],
  );

  return <LiveOverviewContext.Provider value={value}>{children}</LiveOverviewContext.Provider>;
};

export function useLiveOverview(): LiveOverviewContextValue {
  return useContext(LiveOverviewContext);
}

export default LiveOverviewContext;
